import { type FormEvent, Fragment, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { CampoGaleria } from '../componentes/Imagenes';
import { fechaCorta, mxn } from '../formato';
import type { Pieza } from './Piezas';
import type { Vehiculo } from './tipos';
import { ESTADO_PUBLICACION, type Publicacion, useFiltro } from './comercial-comun';

// --- Publicaciones ---------------------------------------------------------------------------

/** Publicaciones de autos y piezas: aparecen en la tienda y, publicadas, en el marketplace. */
export function Publicaciones() {
  const api = useApi();
  const [estado, poner] = useFiltro();
  const lista = useConsulta<Publicacion[]>(
    `/commerce/publicaciones${estado ? `?estado=${estado}` : ''}`,
  );
  const [error, setError] = useState<string | null>(null);
  const [conFotos, setConFotos] = useState<string | null>(null);
  const accion = async (ruta: string, cuerpo?: object) => {
    setError(null);
    try {
      await api('POST', ruta, cuerpo);
      lista.recargar();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <>
      <div className="encabezado">
        <div>
          <h1>Publicaciones</h1>
          <div className="subtitulo">
            Lo que ven tus compradores en tu tienda y en el marketplace
          </div>
        </div>
        <div className="relleno" />
        <Link className="boton primario" to="/publicaciones/nueva">
          Publicar
        </Link>
      </div>
      {error && <p className="error">{error}</p>}
      <section className="seccion">
        <header>
          <select value={estado} onChange={(e) => poner(e.target.value)} aria-label="Estado">
            <option value="">Todas</option>
            {Object.entries(ESTADO_PUBLICACION).map(([c, [t]]) => (
              <option key={c} value={c}>
                {t}
              </option>
            ))}
          </select>
        </header>
        {lista.datos && !lista.datos.length ? (
          <p className="vacio">Sin publicaciones.</p>
        ) : (
          <table>
            <tbody>
              {(lista.datos ?? []).map((p) => {
                const [texto, clase] = ESTADO_PUBLICACION[p.estado];
                const editable = p.estado === 'borrador' || p.estado === 'publicada';
                return (
                  <Fragment key={p.id}>
                    <tr>
                      <td>
                        {p.titulo}
                        <div className="vin">
                          {p.tipo === 'auto' ? 'Auto' : 'Pieza'} ·{' '}
                          <Link to={`/vehiculos/${p.vehiculoId}`}>{p.vin ?? 'vehículo'}</Link> ·{' '}
                          {p.estado === 'publicada' && (
                            <>
                              <Link to={`/canales?publicacion=${p.id}`}>canales</Link> ·{' '}
                            </>
                          )}
                          {fechaCorta(p.publicadaEn ?? p.creadaEn)} ·{' '}
                          {editable ? (
                            <button
                              type="button"
                              className="enlace"
                              onClick={() => setConFotos(conFotos === p.id ? null : p.id)}
                            >
                              {p.fotos.length ? `${p.fotos.length} fotos` : 'agregar fotos'}
                            </button>
                          ) : (
                            `${p.fotos.length} fotos`
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`etiqueta ${clase}`}>{texto}</span>
                      </td>
                      <td className="num">{mxn(p.precioMxn)}</td>
                      <td className="num">
                        {p.estado === 'borrador' || p.estado === 'retirada' ? (
                          <button
                            className="boton"
                            onClick={() => void accion(`/commerce/publicaciones/${p.id}/publicar`)}
                          >
                            Publicar
                          </button>
                        ) : p.estado === 'publicada' ? (
                          <button
                            className="boton"
                            onClick={() => {
                              const motivo = window.prompt(
                                'Motivo del retiro',
                                'Ya no está disponible',
                              );
                              if (motivo)
                                void accion(`/commerce/publicaciones/${p.id}/retirar`, { motivo });
                            }}
                          >
                            Retirar
                          </button>
                        ) : null}
                      </td>
                    </tr>
                    {conFotos === p.id && (
                      <tr>
                        <td colSpan={4}>
                          <EditorFotos publicacion={p} alTerminar={() => lista.recargar()} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

/** Fotos de una publicación: cada cambio se guarda y, si está publicada, se vuelve a anunciar. */
function EditorFotos({
  publicacion,
  alTerminar,
}: {
  publicacion: Publicacion;
  alTerminar: () => void;
}) {
  const api = useApi();
  const [estado, setEstado] = useState<string | null>(null);
  const guardar = async (fotos: string[]) => {
    setEstado('Guardando…');
    try {
      await api('PATCH', `/commerce/publicaciones/${publicacion.id}`, { fotos });
      setEstado('Guardado');
      alTerminar();
    } catch (e) {
      setEstado((e as Error).message);
    }
  };
  return (
    <div>
      <CampoGaleria
        uso="publicacion"
        rotulo="Fotos"
        ayuda="La primera es la principal en tu tienda y en el marketplace."
        inicial={publicacion.fotos}
        maximo={30}
        alCambiar={(f) => void guardar(f)}
      />
      {estado && <small className="subtitulo">{estado}</small>}
    </div>
  );
}

/** Nueva publicación de un auto (por su VIN) o de una pieza del inventario. */
export function NuevaPublicacion() {
  const api = useApi();
  const [params] = useSearchParams();
  const [tipo, setTipo] = useState<'auto' | 'pieza'>(params.get('pieza') ? 'pieza' : 'auto');
  const vehiculos = useConsulta<Vehiculo[]>(tipo === 'auto' ? '/inventory/vehiculos' : null);
  const piezas = useConsulta<Pieza[]>(
    tipo === 'pieza' ? '/inventory/piezas?estado=en_inventario' : null,
  );
  const [error, setError] = useState<string | null>(null);
  const [hecho, setHecho] = useState<Publicacion | null>(null);
  const [enviando, setEnviando] = useState(false);

  const publicar = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const d = new FormData(e.currentTarget);
    setError(null);
    setEnviando(true);
    const comun = {
      titulo: d.get('titulo'),
      precioMxn: d.get('precio'),
      borrador: d.get('borrador') === 'si',
      ...(d.get('descripcion') && { descripcion: d.get('descripcion') }),
      ...(d.get('condicion') && { condicion: d.get('condicion') }),
      fotos: JSON.parse(String(d.get('fotos') || '[]')) as string[],
    };
    try {
      if (tipo === 'auto') {
        const v = (vehiculos.datos ?? []).find((x) => x.id === d.get('vehiculo'));
        if (!v) throw new Error('Elige un vehículo');
        setHecho(
          await api<Publicacion>('POST', '/commerce/publicaciones', {
            tipo: 'auto',
            vehiculoId: v.id,
            vin: v.vin,
            tituloLegal: d.get('tituloLegal'),
            compatibilidad: {
              ...(v.anio && { anio: v.anio }),
              ...(v.marca && { marca: v.marca }),
              ...(v.modelo && { modelo: v.modelo }),
            },
            ...(v.kilometraje !== null && { kilometraje: v.kilometraje }),
            ...comun,
          }),
        );
      } else {
        const p = (piezas.datos ?? []).find((x) => x.id === d.get('pieza'));
        if (!p) throw new Error('Elige una pieza en inventario');
        setHecho(
          await api<Publicacion>('POST', '/commerce/publicaciones', {
            tipo: 'pieza',
            vehiculoId: p.vehiculoId,
            piezaId: p.id,
            ...(p.numeroParte && { numeroParte: p.numeroParte }),
            ...comun,
          }),
        );
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  if (hecho)
    return (
      <div className="entrada" style={{ margin: '40px auto' }}>
        <h1>{hecho.estado === 'publicada' ? 'Publicada' : 'Guardada como borrador'}</h1>
        <p className="subtitulo">{hecho.titulo}</p>
        <Link className="boton primario" to="/publicaciones">
          Ver publicaciones
        </Link>
      </div>
    );
  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/publicaciones">Publicaciones</Link> ›
          </div>
          <h1>Publicar</h1>
        </div>
      </div>
      <form className="seccion cuerpo formulario" onSubmit={(e) => void publicar(e)}>
        <div style={{ display: 'flex', gap: 16 }}>
          <label>
            <input type="radio" checked={tipo === 'auto'} onChange={() => setTipo('auto')} /> Auto
          </label>
          <label>
            <input type="radio" checked={tipo === 'pieza'} onChange={() => setTipo('pieza')} />{' '}
            Pieza
          </label>
        </div>
        {tipo === 'auto' ? (
          <>
            <label className="campo">
              <span>Vehículo</span>
              <select name="vehiculo" required defaultValue={params.get('vehiculo') ?? ''}>
                <option value="" disabled>
                  Elige…
                </option>
                {(vehiculos.datos ?? [])
                  .filter((v) => !v.baja)
                  .map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.vin} · {[v.anio, v.marca, v.modelo].filter(Boolean).join(' ')}
                    </option>
                  ))}
              </select>
            </label>
            <label className="campo">
              <span>Título legal</span>
              <select name="tituloLegal" defaultValue="salvage">
                <option value="limpio">Limpio</option>
                <option value="salvage">Salvage</option>
                <option value="rebuilt">Rebuilt</option>
                <option value="otro">Otro</option>
              </select>
            </label>
          </>
        ) : (
          <label className="campo">
            <span>Pieza en inventario</span>
            <select name="pieza" required defaultValue={params.get('pieza') ?? ''}>
              <option value="" disabled>
                Elige…
              </option>
              {(piezas.datos ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nombre} · {p.codigo} · {p.vin}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="campo">
          <span>Título</span>
          <input name="titulo" required minLength={5} maxLength={120} />
        </label>
        <label className="campo">
          <span>Descripción</span>
          <textarea name="descripcion" maxLength={4000} rows={3} />
        </label>
        <label className="campo">
          <span>Condición</span>
          <input name="condicion" maxLength={40} placeholder="Usada, buen estado" />
        </label>
        <label className="campo">
          <span>Precio (MXN)</span>
          <input name="precio" required inputMode="decimal" pattern="\d{1,10}(\.\d{1,2})?" />
        </label>
        <CampoGaleria
          uso="publicacion"
          rotulo="Fotos"
          ayuda="Desde tu teléfono o tu computadora; la primera es la principal. Se optimizan y se les quita la ubicación."
          nombre="fotos"
          inicial={[]}
          maximo={30}
        />
        <label style={{ display: 'flex', gap: 8 }}>
          <input type="checkbox" name="borrador" value="si" /> Guardar como borrador
        </label>
        {error && <p className="error">{error}</p>}
        <div>
          <button className="boton primario" disabled={enviando}>
            {enviando ? 'Guardando…' : 'Publicar'}
          </button>
        </div>
      </form>
    </>
  );
}
